import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { brandColors } from '../../theme/theme';
import { getAllCompanies, createCompany, updateCompany } from '../../lib/services/enterpriseCompanyService';
import { useAuth } from '../../contexts/AuthContext';
import { SUBSCRIPTION_STATUS } from '../../lib/filmSubscriptionTiers';

/**
 * EnterpriseCompanies Admin Page
 *
 * Manage Enterprise companies
 */
const EnterpriseCompanies = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Create/Edit dialog state
  const [dialog, setDialog] = useState({ open: false, mode: 'create', company: null });
  const [formData, setFormData] = useState({
    companyName: '',
    totalSeats: 10,
    billingEmail: '',
    subscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
    expiresAt: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const companyData = await getAllCompanies();
      setCompanies(companyData);
    } catch (err) {
      console.error('Error fetching companies:', err);
      
      // Provide more specific error messages
      if (err.code === 'permission-denied' || err.message?.includes('permission')) {
        setError('Permission denied: You do not have access to view enterprise companies. Please contact an administrator if you believe this is an error.');
      } else if (err.message) {
        setError(`Failed to load companies: ${err.message}`);
      } else {
        setError('Failed to load companies. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setFormData({
      companyName: '',
      totalSeats: 10,
      billingEmail: '',
      subscriptionStatus: SUBSCRIPTION_STATUS.ACTIVE,
      expiresAt: ''
    });
    setDialog({ open: true, mode: 'create', company: null });
  };

  const handleEditClick = (company) => {
    setFormData({
      companyName: company.companyName,
      totalSeats: company.totalSeats,
      billingEmail: company.billingEmail,
      subscriptionStatus: company.subscriptionStatus,
      expiresAt: company.subscriptionExpiresAt ? 
        new Date(company.subscriptionExpiresAt.toDate()).toISOString().split('T')[0] : ''
    });
    setDialog({ open: true, mode: 'edit', company });
  };

  const handleSave = async () => {
    try {
      const expiresAt = formData.expiresAt ? new Date(formData.expiresAt) : null;
      
      if (dialog.mode === 'create') {
        await createCompany({
          ...formData,
          subscriptionExpiresAt: expiresAt
        }, currentUser.uid);
        alert('Company created successfully');
      } else {
        await updateCompany(dialog.company.id, {
          companyName: formData.companyName,
          totalSeats: formData.totalSeats,
          billingEmail: formData.billingEmail,
          subscriptionStatus: formData.subscriptionStatus,
          subscriptionExpiresAt: expiresAt
        });
        alert('Company updated successfully');
      }
      
      setDialog({ open: false, mode: 'create', company: null });
      fetchCompanies();
    } catch (err) {
      console.error('Error saving company:', err);
      alert(`Failed to ${dialog.mode} company: ${err.message}`);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case SUBSCRIPTION_STATUS.ACTIVE: return 'success';
      case SUBSCRIPTION_STATUS.TRIAL: return 'warning';
      case SUBSCRIPTION_STATUS.EXPIRED: return 'error';
      case SUBSCRIPTION_STATUS.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getSeatUsageColor = (used, total) => {
    const percentage = (used / total) * 100;
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  if (loading && companies.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: brandColors.primary }}>
            Enterprise Companies
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage enterprise company accounts and seat allocations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{ bgcolor: brandColors.primary }}
        >
          Create Enterprise Company
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700, color: brandColors.primary }}>
              {companies.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Companies
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#4caf50' }}>
              {companies.filter(c => c.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Companies
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#ff9800' }}>
              {companies.reduce((sum, c) => sum + (c.usedSeats || 0), 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Seats Used
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2196f3' }}>
              {companies.reduce((sum, c) => sum + (c.totalSeats || 0), 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Seats Available
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Companies Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Companies ({companies.length})
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCompanies}
              disabled={loading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Seats</TableCell>
                  <TableCell>Admins</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {company.companyName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {company.billingEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.subscriptionStatus || 'active'}
                        color={getStatusColor(company.subscriptionStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={`${company.usedSeats || 0} / ${company.totalSeats || 0}`}
                        color={getSeatUsageColor(company.usedSeats || 0, company.totalSeats || 0)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {company.enterpriseAdminUids?.length || 0} admin(s)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(company.subscriptionExpiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(company.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/enterprise-companies/${company.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Company">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(company)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {companies.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <BusinessIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                No enterprise companies yet
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleCreateClick}
                sx={{ mt: 2 }}
              >
                Create First Enterprise Company
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'create', company: null })} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.mode === 'create' ? 'Create New Company' : 'Edit Company'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.companyName}
              onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Billing Email"
              type="email"
              value={formData.billingEmail}
              onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
              required
            />
            
            <TextField
              fullWidth
              label="Total Seats"
              type="number"
              value={formData.totalSeats}
              onChange={(e) => setFormData({ ...formData, totalSeats: parseInt(e.target.value) })}
              inputProps={{ min: 1 }}
              required
            />
            
            <TextField
              fullWidth
              label="Expires At"
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for no expiration"
            />
            
            {dialog.mode === 'edit' && (
              <Alert severity="info">
                To manage users and admins, go to the company detail page.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, mode: 'create', company: null })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.companyName || !formData.billingEmail}
          >
            {dialog.mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EnterpriseCompanies;

